import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { quotesService } from '../../api/apiClient';
import { useToast } from '../../context/ToastContext';
import { Card, CardHeader, CardTitle, CardBody } from '../../components/UI/Card';
import { Button } from '../../components/UI/Button';
import { Input, Textarea, Select } from '../../components/UI/Input';
import { Spinner } from '../../components/UI/Spinner';
import {
  Plus,
  Trash2,
  Save,
  ArrowLeft,
  UserPlus,
} from 'lucide-react';

export const QuoteForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { success, error: toastError } = useToast();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Reference data
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [products, setProducts] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  // Form State
  const [formData, setFormData] = useState({
    quote_date: new Date().toISOString().split('T')[0],
    valid_until: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    customer_id: '',
    project_id: '',
    currency_id: '1',
    notes: '',
    // Inline new customer fields
    is_new_customer: false,
    customer_name: '',
    customer_email: '',
    customer_address1: '',
    customer_address2: '',
  });

  // Items State
  const [items, setItems] = useState([
    { product_id: '', description: '', quantity: 1, price: 0, total: 0 }
  ]);

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const loadFormData = async () => {
      setLoading(true);
      try {
        const createRes = await quotesService.getCreateData();
        if (createRes && createRes.data) {
          setCustomers(createRes.data.customers || []);
          setProjects(createRes.data.projects || []);
          setProducts(createRes.data.products || []);
          setCurrencies(createRes.data.currencies || []);
          
          if (createRes.data.currencies?.length > 0) {
            setFormData(prev => ({
              ...prev,
              currency_id: prev.currency_id || String(createRes.data.currencies[0].id)
            }));
          }
        }

        if (isEdit) {
          const editRes = await quotesService.get(id);
          if (editRes && editRes.data && editRes.data.quote) {
            const q = editRes.data.quote;
            setFormData({
              quote_date: q.quote_date || '',
              valid_until: q.valid_until || '',
              customer_id: String(q.customer_id || ''),
              project_id: q.project_id ? String(q.project_id) : '',
              currency_id: String(q.currency_id || '1'),
              notes: q.notes || '',
              is_new_customer: false,
              customer_name: '',
              customer_email: '',
              customer_address1: '',
              customer_address2: '',
            });

            if (q.quoteItems && Array.isArray(q.quoteItems) && q.quoteItems.length > 0) {
              setItems(q.quoteItems.map(item => ({
                product_id: item.product_id ? String(item.product_id) : '',
                description: item.description || '',
                quantity: Number(item.quantity || 1),
                price: Number(item.price || 0),
                total: Number(item.price || 0) * Number(item.quantity || 1)
              })));
            }
          }
        }
      } catch (err) {
        toastError(err.message || 'Error al cargar datos del formulario.');
      } finally {
        setLoading(false);
      }
    };

    loadFormData();
  }, [id, isEdit, toastError]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleItemChange = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;

    // If product was selected, auto-fill description and unit price
    if (field === 'product_id') {
      const selectedProd = products.find(p => String(p.id) === String(value));
      if (selectedProd) {
        updated[index].description = selectedProd.name;
        updated[index].price = Number(selectedProd.price || 0);
      }
    }

    const qty = parseFloat(updated[index].quantity) || 0;
    const prc = parseFloat(updated[index].price) || 0;
    updated[index].total = Number((qty * prc).toFixed(2));

    setItems(updated);
  };

  const addItemRow = () => {
    setItems(prev => [
      ...prev,
      { product_id: '', description: '', quantity: 1, price: 0, total: 0 }
    ]);
  };

  const removeItemRow = (index) => {
    if (items.length <= 1) return;
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const calculateGrandTotal = () => {
    return items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrors({});

    const total = calculateGrandTotal();

    const payload = {
      quote_date: formData.quote_date,
      valid_until: formData.valid_until,
      project_id: formData.project_id ? formData.project_id : null,
      currency_id: formData.currency_id,
      notes: formData.notes,
      items: JSON.stringify(items),
      total: total,
      customer_id: formData.is_new_customer ? 'new' : formData.customer_id,
      ...(formData.is_new_customer ? {
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_address1: formData.customer_address1,
        customer_address2: formData.customer_address2,
      } : {})
    };

    try {
      if (isEdit) {
        await quotesService.update(id, payload);
        success('Cotización actualizada correctamente.');
      } else {
        await quotesService.create(payload);
        success('Cotización creada exitosamente.');
      }
      navigate('/quotes');
    } catch (err) {
      if (err.errors) {
        setErrors(err.errors);
      }
      toastError(err.data?.message || err.message || 'Error al guardar cotización.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Spinner size="lg" label="Cargando formulario..." className="py-20" />;
  }

  const grandTotal = calculateGrandTotal();
  const selectedCurrency = currencies.find(c => String(c.id) === String(formData.currency_id));
  const currencySymbol = selectedCurrency?.symbol || '$';

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/quotes">
            <Button variant="ghost" size="sm" icon={ArrowLeft}>
              Volver
            </Button>
          </Link>
          <div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100 tracking-tight">
              {isEdit ? 'Editar Cotización' : 'Nueva Cotización'}
            </h1>
            <p className="text-xs text-slate-500">
              {isEdit ? `Modificando registro #${id}` : 'Configura clientes, conceptos y montos'}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Main Document Details Card */}
        <Card>
          <CardHeader>
            <CardTitle subtitle="Información general y vigencia">
              Detalles del Presupuesto
            </CardTitle>
          </CardHeader>

          <CardBody className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Input
                label="Fecha de Emisión"
                type="date"
                name="quote_date"
                value={formData.quote_date}
                onChange={handleInputChange}
                error={errors.quote_date?.[0]}
                required
              />

              <Input
                label="Válido Hasta"
                type="date"
                name="valid_until"
                value={formData.valid_until}
                onChange={handleInputChange}
                error={errors.valid_until?.[0]}
              />

              <Select
                label="Moneda"
                name="currency_id"
                value={formData.currency_id}
                onChange={handleInputChange}
                error={errors.currency_id?.[0]}
                required
              >
                {currencies.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.code} ({c.symbol}) - {c.name}
                  </option>
                ))}
              </Select>

              <Select
                label="Proyecto (Opcional)"
                name="project_id"
                value={formData.project_id}
                onChange={handleInputChange}
                error={errors.project_id?.[0]}
              >
                <option value="">-- Sin Proyecto Asociado --</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </Select>
            </div>

            {/* Customer Section */}
            <div className="pt-3 border-t border-slate-100 dark:border-slate-800">
              <div className="flex items-center justify-between mb-3">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Cliente Destinatario
                </label>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, is_new_customer: !prev.is_new_customer }))}
                  className="text-xs text-[#08707A] dark:text-teal-400 font-bold hover:underline flex items-center gap-1"
                >
                  <UserPlus className="w-3.5 h-3.5" />
                  {formData.is_new_customer ? 'Seleccionar Cliente Existente' : '+ Registrar Cliente Nuevo Aquí'}
                </button>
              </div>

              {!formData.is_new_customer ? (
                <Select
                  name="customer_id"
                  value={formData.customer_id}
                  onChange={handleInputChange}
                  error={errors.customer_id?.[0]}
                  required={!formData.is_new_customer}
                >
                  <option value="">-- Selecciona un Cliente del Directorio --</option>
                  {customers.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.full_name} {c.email ? `(${c.email})` : ''}
                    </option>
                  ))}
                </Select>
              ) : (
                <div className="p-4 rounded-xl bg-teal-50/50 dark:bg-teal-950/30 border border-teal-100 dark:border-teal-900/60 space-y-3 animate-fade-in">
                  <Input
                    label="Nombre Completo o Razón Social"
                    name="customer_name"
                    placeholder="Empresa / Contacto"
                    value={formData.customer_name}
                    onChange={handleInputChange}
                    error={errors.customer_name?.[0]}
                    required={formData.is_new_customer}
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input
                      label="Correo Electrónico"
                      type="email"
                      name="customer_email"
                      placeholder="cliente@email.com"
                      value={formData.customer_email}
                      onChange={handleInputChange}
                    />
                    <Input
                      label="Dirección Principal"
                      name="customer_address1"
                      placeholder="Av. Principal #456"
                      value={formData.customer_address1}
                      onChange={handleInputChange}
                      error={errors.customer_address1?.[0]}
                      required={formData.is_new_customer}
                    />
                  </div>
                </div>
              )}
            </div>
          </CardBody>
        </Card>

        {/* Dynamic Items Card */}
        <Card>
          <CardHeader actions={
            <Button type="button" variant="secondary" size="sm" icon={Plus} onClick={addItemRow}>
              Agregar Concepto
            </Button>
          }>
            <CardTitle subtitle="Detalle de productos o servicios cotizados">
              Conceptos & Partidas
            </CardTitle>
          </CardHeader>

          <CardBody className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 font-bold uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
                  <tr>
                    <th className="py-3 px-4 w-44">Producto Catálogo</th>
                    <th className="py-3 px-4">Descripción del Concepto</th>
                    <th className="py-3 px-4 w-24">Cantidad</th>
                    <th className="py-3 px-4 w-32">Precio Unit.</th>
                    <th className="py-3 px-4 w-32 text-right">Subtotal</th>
                    <th className="py-3 px-4 w-12 text-center"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {items.map((item, idx) => (
                    <tr key={idx} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
                      <td className="py-2.5 px-4">
                        <select
                          className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-2 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:ring-1 focus:ring-brand-500 focus:outline-none"
                          value={item.product_id}
                          onChange={(e) => handleItemChange(idx, 'product_id', e.target.value)}
                        >
                          <option value="">(Manual)</option>
                          {products.map(p => (
                            <option key={p.id} value={p.id}>{p.name} (${Number(p.price).toFixed(2)})</option>
                          ))}
                        </select>
                      </td>

                      <td className="py-2.5 px-4">
                        <input
                          type="text"
                          required
                          placeholder="Descripción detallada..."
                          className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:ring-1 focus:ring-brand-500 focus:outline-none"
                          value={item.description}
                          onChange={(e) => handleItemChange(idx, 'description', e.target.value)}
                        />
                      </td>

                      <td className="py-2.5 px-4">
                        <input
                          type="number"
                          step="any"
                          min="0.01"
                          required
                          className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-2 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:ring-1 focus:ring-brand-500 focus:outline-none text-right font-mono"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(idx, 'quantity', e.target.value)}
                        />
                      </td>

                      <td className="py-2.5 px-4">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          required
                          className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-2 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:ring-1 focus:ring-brand-500 focus:outline-none text-right font-mono font-bold"
                          value={item.price}
                          onChange={(e) => handleItemChange(idx, 'price', e.target.value)}
                        />
                      </td>

                      <td className="py-2.5 px-4 text-right font-black text-slate-900 dark:text-slate-100 font-mono text-sm">
                        {currencySymbol} {Number(item.total).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>

                      <td className="py-2.5 px-4 text-center">
                        <button
                          type="button"
                          disabled={items.length <= 1}
                          onClick={() => removeItemRow(idx)}
                          className="text-slate-400 hover:text-red-600 disabled:opacity-30 disabled:hover:text-slate-400 transition-colors p-1"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Total Footer Banner */}
            <div className="p-4 bg-slate-50 dark:bg-slate-800/40 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Total General Cotizado
              </span>
              <span className="text-xl font-black text-slate-900 dark:text-slate-100 font-mono">
                {currencySymbol} {Number(grandTotal).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>
          </CardBody>
        </Card>

        {/* Notes & Submit */}
        <Card>
          <CardBody className="space-y-4">
            <Textarea
              label="Términos, Condiciones y Notas de la Cotización"
              name="notes"
              placeholder="Ej: Precios válidos por 15 días. 50% de anticipo para iniciar trabajos..."
              value={formData.notes}
              onChange={handleInputChange}
              rows={3}
            />

            <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100 dark:border-slate-800">
              <Link to="/quotes">
                <Button variant="secondary" disabled={submitting}>
                  Cancelar
                </Button>
              </Link>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                icon={Save}
                isLoading={submitting}
                className="bg-[#08707A] hover:bg-[#065B63]"
              >
                {isEdit ? 'Guardar Cambios' : 'Emitir Cotización'}
              </Button>
            </div>
          </CardBody>
        </Card>
      </form>
    </div>
  );
};
